// src/utils/carteleria/cierresProyeccionCarteleria.js
//
// F3c — Proyección de ventas, vista CIERRES (hardware y cartelería): implementación NUEVA que lee del motor
// `calendarioNegocio` (montos por pantalla, monedas, plazo) y convierte con el conversor de la superficie. La
// usa `useVPCierresCalcs` SOLO para los meses >= CORTE_HISTORICO (2026-10); los anteriores salen del histórico
// congelado (`./historico/proyeccionCierresHistorico`).
//
// Semántica (idéntica a la de antes — "lo histórico no se toca"):
//  * hardware: `monto_hardware` en el mes de `fecha_cierre` (requiere fecha_cierre; estados 100%/Activo);
//  * cartelería: (gestión + contenidos) × plazo en el mes de `fecha_cierre`; los contenidos con rango propio
//    (`contenidos_fecha_inicio`) van aparte, × meses del rango, en el mes de `contenidos_fecha_inicio`;
//  * moneda por defecto UF (Chile) / PEN (otro) cuando el negocio no la carga; la de contenidos NO hereda la de
//    gestión (como antes). El orden de las operaciones se conserva para que el resultado sea bit-idéntico.
import { calendarioNegocio, MODO_EXTENDIDO } from "./calendarioNegocio";
import { convertirMontoCarteleriaPorFecha } from "./convertirMontoCarteleria";

export const ESTADOS_CIERRE = ["100%", "Activo"];

// Último día del mes (local) en "YYYY-MM-DD" — la fecha a la que se convierte, como siempre.
export const fechaConversionMes = (ano, mesIdx) => new Date(ano, mesIdx + 1, 0).toISOString().split("T")[0];

/**
 * Componentes de cierre de un negocio de cartelería, leídos del motor (moneda ORIGINAL, sin convertir).
 * @param {object} n fila de negocios_carteleria
 * @param {object} cal `calendarioNegocio(n, { hoy })`
 * @param {string} defaultMoneda moneda cuando el negocio no la carga ("UF" / "PEN")
 */
export const componentesCierreCarteleria = (n, cal, defaultMoneda) => {
  const feeGestion = cal.porPantalla.gestion.monto;
  const feeContenidos = cal.porPantalla.contenidos.monto;
  const tieneRangoPropio = Boolean(n.incluye_contenidos && n.contenidos_fecha_inicio);
  return {
    pantallas: cal.pantallas,
    plazo: cal.plazo,
    // En modo extendido (fecha_fin propia / precio total) el plazo firmado es el span del motor.
    extendido: cal.modo === MODO_EXTENDIDO,
    mesesContrato: cal.totalPlazo.meses,
    totalGestionExtendido: cal.totalPlazo.gestion.monto,
    feeGestion,
    feeContenidos,
    feeContenidosEnBase: tieneRangoPropio ? 0 : feeContenidos,
    tieneRangoPropio,
    monedaGestion: (cal.monedas.gestion || defaultMoneda).toUpperCase(),
    monedaContenidos: (cal.monedas.contenidosCargada || defaultMoneda).toUpperCase(),
  };
};

/**
 * Total de cierre del contrato base en moneda local: (gestión + contenidos-en-base) × plazo.
 * Modo legado: igual que antes, (conv(gestión×pant) + conv(contenidos×pant)) × plazo.
 * Modo extendido: gestión = su total del contrato (precio total o fee × meses del span); contenidos × meses.
 */
export const totalCierreBaseLocal = (c, paisParam, fechaConversion) => {
  const contenidosLocal = convertirMontoCarteleriaPorFecha(
    c.feeContenidosEnBase * c.pantallas,
    c.monedaContenidos,
    paisParam,
    fechaConversion
  );
  if (c.extendido) {
    const gestionTotalLocal = convertirMontoCarteleriaPorFecha(
      c.totalGestionExtendido,
      c.monedaGestion,
      paisParam,
      fechaConversion
    );
    return gestionTotalLocal + contenidosLocal * c.mesesContrato;
  }
  const gestionLocal = convertirMontoCarteleriaPorFecha(
    c.feeGestion * c.pantallas,
    c.monedaGestion,
    paisParam,
    fechaConversion
  );
  return (gestionLocal + contenidosLocal) * c.plazo;
};

/** Meses del rango propio de contenidos (o el plazo si no hay fin), como siempre. */
export const plazoContenidosPropio = (n, plazo) => {
  const fechaCont = new Date(n.contenidos_fecha_inicio + "T00:00:00");
  const fechaFin = n.contenidos_fecha_fin ? new Date(n.contenidos_fecha_fin + "T00:00:00") : null;
  return fechaFin
    ? (fechaFin.getFullYear() - fechaCont.getFullYear()) * 12 + (fechaFin.getMonth() - fechaCont.getMonth()) + 1
    : plazo;
};

/** Hardware cerrado por mes (moneda local). Mismo contrato que `calcCierresHW` del hook. */
export const calcCierresHWMotor = (data, paisParam, ano, hoy = new Date()) => {
  const valores = Array(12).fill(0);
  data
    .filter((n) => n.incluye_hardware && !n.eliminada && ESTADOS_CIERRE.includes(n.estado) && n.fecha_cierre)
    .forEach((n) => {
      const fecha = new Date(n.fecha_cierre + "T00:00:00");
      if (fecha.getFullYear() !== ano) return;
      const cal = calendarioNegocio(n, { hoy });
      const defaultMoneda = paisParam === "Chile" ? "UF" : "PEN";
      const moneda = (cal.monedas.hardware || defaultMoneda).toUpperCase();
      // El evento CAPEX del motor trae el monto; un OPEX con hardware cargado conserva `monto_hardware` (como antes).
      const montoHW = cal.hardwareCapex ? cal.hardwareCapex.monto : Number(n.monto_hardware) || 0;
      valores[fecha.getMonth()] += convertirMontoCarteleriaPorFecha(
        montoHW,
        moneda,
        paisParam,
        fechaConversionMes(ano, fecha.getMonth())
      );
    });
  return valores;
};

/** Cartelería cerrada por mes (moneda local). Mismo contrato que `calcCierresCart` del hook. */
export const calcCierresCartMotor = (data, paisParam, ano, hoy = new Date()) => {
  const valores = Array(12).fill(0);
  data
    .filter((n) => !n.eliminada && ESTADOS_CIERRE.includes(n.estado))
    .forEach((n) => {
      const cal = calendarioNegocio(n, { hoy });
      const defaultMoneda = paisParam === "Chile" ? "UF" : "PEN";
      const c = componentesCierreCarteleria(n, cal, defaultMoneda);

      // Cierre del contrato base (gestión + contenidos si no tiene rango separado)
      if (n.fecha_cierre) {
        const fecha = new Date(n.fecha_cierre + "T00:00:00");
        if (fecha.getFullYear() === ano) {
          valores[fecha.getMonth()] += totalCierreBaseLocal(c, paisParam, fechaConversionMes(ano, fecha.getMonth()));
        }
      }

      // Cierre adicional de contenidos con fecha de inicio propia
      if (c.tieneRangoPropio) {
        const fechaCont = new Date(n.contenidos_fecha_inicio + "T00:00:00");
        if (fechaCont.getFullYear() === ano) {
          const contenidosLocal = convertirMontoCarteleriaPorFecha(
            c.feeContenidos * c.pantallas,
            c.monedaContenidos,
            paisParam,
            fechaConversionMes(ano, fechaCont.getMonth())
          );
          valores[fechaCont.getMonth()] += contenidosLocal * plazoContenidosPropio(n, c.plazo);
        }
      }
    });
  return valores;
};
